import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  generateMultiplicationTask,
  loadLastTaskForUser,
  saveLastTaskForUser,
  recordAttempt,
  getAllAttempts,
} from "../lib/rounding";
import type { Task, AttemptEvent } from "../lib/rounding";

const MultiplicationPage: React.FC = () => {
  const { user } = useAuth();
  const username = user?.username ?? "guest";

  const [task, setTask] = useState<Task | null>(null);
  const [input, setInput] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [attempts, setAttempts] = useState<any[]>([]);

  const startRef = useRef<number | null>(null);
  const focusLostRef = useRef(false);
  const eventsRef = useRef<AttemptEvent[]>([]);
  const lastActivityRef = useRef<number>(Date.now());
  const idleRef = useRef(false);
  const idleTimerRef = useRef<number | null>(null);
  const EVENTS_CAP = 2000;

  function pushEvent(type: string, data?: any) {
    const ev: AttemptEvent = { type, ts: Date.now(), data: data ?? null };
    const arr = eventsRef.current;
    arr.push(ev);
    if (arr.length > EVENTS_CAP) arr.splice(0, arr.length - EVENTS_CAP);
  }

  function scheduleIdleTimer() {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current as unknown as number);
    }
    idleTimerRef.current = window.setTimeout(() => {
      idleRef.current = true;
      pushEvent("idle-start", { since: Date.now() - lastActivityRef.current });
    }, 5000);
  }

  function markActivity() {
    const now = Date.now();
    if (idleRef.current) {
      pushEvent("idle-end", { idleFor: now - lastActivityRef.current });
      idleRef.current = false;
    }
    lastActivityRef.current = now;
    scheduleIdleTimer();
  }

  useEffect(() => {
    // load saved task for this user or create a new one
    let cancelled = false;
    (async () => {
      const saved = await loadLastTaskForUser(username);
      if (cancelled) return;
      if (saved && saved.task) {
        const tSaved = saved.task;
        const type = (tSaved.rule as any)?.type;
        if (type === "multiplication") {
          setTask(tSaved);
          setIsCorrect(Boolean(saved.completed));
        } else {
          const t = generateMultiplicationTask();
          setTask(t);
          await saveLastTaskForUser(username, t, false);
        }
      } else {
        const t = generateMultiplicationTask();
        setTask(t);
        await saveLastTaskForUser(username, t, false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  useEffect(() => {
    // refresh attempts for this task
    if (!task) return;
    // start timer when task becomes active
    startRef.current = Date.now();
    setInput("");
    setMessage(null);
    focusLostRef.current = false;

    function onVisibilityChange() {
      if (document.hidden) {
        focusLostRef.current = true;
        pushEvent("blur", { reason: "visibilitychange" });
      } else {
        pushEvent("focus", { reason: "visibilitychange" });
      }
      markActivity();
    }
    function onBlur() {
      focusLostRef.current = true;
      pushEvent("blur", { reason: "window-blur" });
      markActivity();
    }

    function onFocus() {
      pushEvent("focus", { reason: "window-focus" });
      markActivity();
    }

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // start event capture for this task
    eventsRef.current = [];
    pushEvent("task-start", { taskId: task.id });
    markActivity();

    let cancelled = false;
    (async () => {
      const all = await getAllAttempts();
      if (cancelled) return;
      setAttempts(all.filter((a) => a.taskId === task.id && a.user === username));
    })();

    return () => {
      cancelled = true;
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current as unknown as number);
    };
  }, [task, username]);

  function normalizeNumberString(s: string) {
    if (s == null) return NaN;
    const cleaned = s.replace(/\s/g, "");
    const n = Number(cleaned);
    return isNaN(n) ? NaN : n;
  }

  function checkCorrect(t: Task, submission: string) {
    const expected = normalizeNumberString(t.answer);
    const got = normalizeNumberString(submission);
    if (isNaN(got) || isNaN(expected)) {
      // fall back to string comparison (trim spaces)
      return t.answer.replace(/\s/g, "") === submission.replace(/\s/g, "");
    }
    return Math.abs(expected - got) < 1e-9;
  }

  const onCheck = async () => {
    if (!task) return;
    const now = Date.now();
    const timeMs = startRef.current ? now - startRef.current : 0;
    const correct = checkCorrect(task, input.trim());

    // record a submit event and include captured events
    pushEvent("submit", { submission: input.trim() });
    const eventsToSend = eventsRef.current.slice();

    await recordAttempt({
      user: username,
      taskId: task.id,
      taskText: task.text,
      rule: task.rule,
      submission: input.trim(),
      correct,
      timeMs,
      focusLost: focusLostRef.current,
      events: eventsToSend,
    });
    // reset input and focus flag for the next attempt
    setInput("");
    focusLostRef.current = false;

    // clear captured events after successfully recording
    eventsRef.current = [];

    const all = await getAllAttempts();
    setAttempts(all.filter((a) => a.taskId === task.id && a.user === username));

    if (correct) {
      setMessage("Correct — well done!");
      setIsCorrect(true);
      await saveLastTaskForUser(username, task, true);
    } else {
      setMessage("Incorrect — try again.");
    }
  };

  const onNext = () => {
    const t = generateMultiplicationTask();
    setTask(t);
    setIsCorrect(false);
    saveLastTaskForUser(username, t, false);
    // timer and attempts will be setup by useEffect
  };

  return (
    <div className="w-full mt-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle>Math - Multiplication</CardTitle>
          <CardDescription>Practice multiplication problems. Answers are saved and attempts recorded.</CardDescription>
        </CardHeader>

        <CardContent>
          {!task ? (
            <p>Loading...</p>
          ) : (
            <div className="space-y-4">
              <div className="text-lg font-semibold">{task.text}</div>

              {isCorrect ? (
                <div className="text-green-600">You have completed this task. Press Next to continue.</div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter your answer (e.g. 12)"
                    value={input}
                    onChange={(e) => {
                      const v = (e.target as HTMLInputElement).value;
                      setInput(v);
                      pushEvent("input", { len: v.length, caret: (e.target as HTMLInputElement).selectionStart });
                      markActivity();
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      pushEvent("keydown", { key: (e as any).key, code: (e as any).code, ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey });
                      markActivity();
                    }}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                      try {
                        const txt = e.clipboardData.getData("text");
                        pushEvent("paste", { len: txt.length });
                      } catch {
                        pushEvent("paste", null);
                      }
                      markActivity();
                    }}
                  />
                  <Button onClick={onCheck}>Check</Button>
                </div>
              )}

              {message && <div className="text-sm text-muted-foreground">{message}</div>}

              <div className="text-sm">
                Attempts for this task: <strong>{attempts.length}</strong>
              </div>

              {attempts.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Last attempt: {new Date(attempts[attempts.length - 1].timestamp).toLocaleString()} — submission: {attempts[attempts.length - 1].submission} — {attempts[attempts.length - 1].correct ? "correct" : "incorrect"}
                </div>
              )}
            </div>
          )}
        </CardContent>

        <CardFooter className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => saveLastTaskForUser(username, task as Task, !!isCorrect)}>
              Save progress
            </Button>
            <Button onClick={onNext} disabled={!isCorrect}>
              Next
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default MultiplicationPage;
